import React, { useState } from "react";
import styled from "styled-components";
import CreateDocumentForm from "components/document/Create/CreateDocumentForm/CreateDocumentForm";
import { useRecoilValue } from "recoil";
import { useNavigate, useParams } from "react-router-dom";
import { DOCUMENT_CATEGORY_UUID, DOCUMENT_UUID } from "config/constant";
import {
  selectedAnalysisCityState,
  selectedAnalysisProvinceState,
} from "store/document/analysisDocument";
import { TempDocumentDetailResponse } from "apis/data-contracts";
import ContractInfoStep1 from "components/document/Create/CreateDocumentForm/docClass/analysis/ContractInfoStep1";
import Toast, { useToast } from "components/foundation/Toast";
import Button from "components/foundation/Button";
import { TempDocumentDetailResponseWithAny } from "features/document/hooks/queries/useTempDocumentDetailQuery";
import BeforeUnloadModal from "components/Common/BeforeUnloadModal";
import useSaveTempDoc from "features/document/hooks/create/useSaveTempDoc";
import useDocumentTempSave from "features/document/hooks/create/useDocumentTempSave";
import Modal from "components/foundation/Modal/Modal";
import useMediaView from "features/common/hooks/useMediaView";
import useHomeButton from "features/document/hooks/create/useHomeButton";
import useReselectAnalysisDoc from "features/document/hooks/create/analysis/useReselectAnalysisDoc";
import useFreeTags from "features/document/hooks/create/useFreeTags";
import useRemoveTempDoc from "features/document/hooks/create/useRemoveTempDoc";
import getTempSaveRequest, {
  DocumentForm,
} from "features/document/utils/getTempSaveRequest";
import useUpdateTempDoc from "features/document/hooks/create/useUpdateTempDoc";
import getTempUpdateRequest from "features/document/utils/getTempUpdateRequest";
import useAnalysisDocumentTags from "features/document/hooks/create/analysis/useAnalysisDocumentTags";
import { useFormContext } from "react-hook-form";
import { ROUTER_PATH } from "config/path";
import { useRequestReviewMutation } from "features/document/hooks/mutations/useRequestReviewMutation";
import {
  DECISION_MANAGER_FIELD_NAME,
  REFERENCE_MEMBER_FIELD_NAME,
} from "config/documentConstant";

const ANALYSIS_DOCUMENT_TITLE = "ANALYSIS";

interface AnalysisCreateFormStep1PageProps {
  tempDocumentDetail?: TempDocumentDetailResponseWithAny;
}

const AnalysisCreateFormStep1Page = ({
  tempDocumentDetail,
}: AnalysisCreateFormStep1PageProps) => {
  const navigate = useNavigate();
  const tempDocumentUuid = useParams().uuid;

  const documentClassUuid =
    tempDocumentDetail?.documentClassUuid ||
    DOCUMENT_UUID.ANALYSIS.classes.수주정보.uuid;

  const {
    selectedProvince,
    selectedCity,
    regionTags,
    businessTags,
    getTagUuids,
  } = useAnalysisDocumentTags();

  const { freeTags, getFreeTagUuids } = useFreeTags({ tempDocumentDetail });
  const [beforeUnloadDisable, setBeforeUnloadDisable] = useState(false);

  const { openTempSaveToast, onOpenChangeTempSaveToast, onTempSaveDoc } =
    useDocumentTempSave({
      tempDocumentDetail,
      documentClassName:
        tempDocumentDetail?.documentClassName ||
        DOCUMENT_UUID.ANALYSIS.classes.수주정보.name,
      documentClassUuid,
      getTagUuids,
      setBeforeUnloadDisable,
    });

  const { openHomeModal, setOpenHomeModal, onClickHomeButton, goToHome } =
    useHomeButton({
      setBeforeUnloadDisable,
    });

  const methods = useFormContext<DocumentForm>();

  const {
    openRemoveTempDocToast,
    onOpenChangeRemoveTempDocToast,
    onClickRemoveTempDoc,
  } = useRemoveTempDoc({ tempDocumentUuid, setBeforeUnloadDisable });

  const approveButtonLabel =
    tempDocumentDetail?.documentApprove?.approveStatus !== "REQ_SUPPLEMENT"
      ? "1단계 결재요청"
      : "수정 후 재요청";

  const {
    openStep1CompleteToast,
    onOpenChangeStep1CompleteToast,
    onClickStep1Complete,
  } = useRegisterAnalysisStep1({
    methods,
    tempDocumentDetail,
    setBeforeUnloadDisable,
    documentClassUuid,
    documentClassName: DOCUMENT_UUID.ANALYSIS.classes.수주정보.name,
    tagUuids: getTagUuids(),
    getFreeTagUuids,
  });

  const { onClickReselectAnalysisDoc } = useReselectAnalysisDoc({
    tempDocumentUuid,
  });

  const isSmallView = useMediaView("small");

  if (isSmallView) {
    navigate(-1);
    return;
  }

  return (
    <>
      <WrapStyle>
        <CreateDocumentForm.Header
          title={ANALYSIS_DOCUMENT_TITLE}
          tempDocumentUuid={tempDocumentUuid}
          docTags={{
            지역: regionTags,
            사업구분: businessTags,
          }}
          freeTags={freeTags}
          actions={
            <Button variant="tertiary" onClick={onClickReselectAnalysisDoc}>
              재선택
            </Button>
          }
        />
        <CreateDocumentForm.Body
          tempDocumentUuid={tempDocumentUuid}
          actions={
            <>
              <Button
                size="large"
                variant="tertiary"
                onClick={onClickHomeButton}
              >
                HOME
              </Button>
              {tempDocumentUuid && (
                <Button
                  size="large"
                  variant="tertiary-neg"
                  onClick={onClickRemoveTempDoc}
                >
                  임시저장 삭제
                </Button>
              )}
              <Button size="large" onClick={onClickStep1Complete}>
                {approveButtonLabel}
              </Button>
            </>
          }
        >
          <Form
            tempDocumentDetail={tempDocumentDetail}
            documentClassUuid={documentClassUuid}
          />
        </CreateDocumentForm.Body>
      </WrapStyle>

      <Modal.Root open={openHomeModal} onOpenChange={setOpenHomeModal}>
        <Modal.Body
          title="작성한 문서를 임시저장 하시겠습니까?"
          onCancel={goToHome}
          cancelValue="나가기"
          onConfirm={() =>
            onTempSaveDoc({
              form: methods.watch(),
              provinceName: selectedProvince,
              cityName: selectedCity || undefined,
            })
          }
          confirmValue="임시저장"
        >
          {"'나가기' 클릭 시 작성한 문서는 등록되지 않습니다."}
        </Modal.Body>
      </Modal.Root>

      <BeforeUnloadModal
        isDirty={methods.formState.isDirty}
        disable={beforeUnloadDisable}
      />

      <Toast.Message
        open={openTempSaveToast}
        onOpenChange={onOpenChangeTempSaveToast}
      >
        해당 문서가 임시저장 되었습니다.
      </Toast.Message>

      <Toast.Message
        open={openRemoveTempDocToast}
        onOpenChange={onOpenChangeRemoveTempDocToast}
      >
        해당 임시 문서가 삭제되었습니다.
      </Toast.Message>

      <Toast.Message
        open={openStep1CompleteToast}
        onOpenChange={onOpenChangeStep1CompleteToast}
      >
        ANALYSIS(수주정보관리) 문서의 1단계 결재 요청이 완료되었습니다.
      </Toast.Message>
    </>
  );
};

export default AnalysisCreateFormStep1Page;

interface FormProps {
  tempDocumentDetail?: TempDocumentDetailResponse;
  documentClassUuid?: string;
}

const Form = ({ documentClassUuid, tempDocumentDetail }: FormProps) => {
  switch (documentClassUuid) {
    case DOCUMENT_UUID.ANALYSIS.classes.수주정보.uuid:
      return <ContractInfoStep1 tempDocumentDetail={tempDocumentDetail} />;
  }
  return null;
};

const useRegisterAnalysisStep1 = ({
  methods,
  tempDocumentDetail,
  setBeforeUnloadDisable,
  documentClassUuid,
  documentClassName,
  tagUuids,
  getFreeTagUuids,
}: {
  methods: any;
  tempDocumentDetail?: TempDocumentDetailResponseWithAny;
  setBeforeUnloadDisable: (disable: boolean) => void;
  documentClassUuid: string;
  documentClassName: string;
  tagUuids: string[];
  getFreeTagUuids: () => string[];
}) => {
  const {
    open: openStep1CompleteToast,
    onOpenToast: onOpenStep1CompleteToast,
    onOpenChange: onOpenChangeStep1CompleteToast,
  } = useToast();

  const { saveTempDoc } = useSaveTempDoc();
  const { updateTempDoc } = useUpdateTempDoc();
  const { mutate: requestReview } = useRequestReviewMutation();

  const selectedProvince = useRecoilValue(selectedAnalysisProvinceState);
  const selectedCity = useRecoilValue(selectedAnalysisCityState);

  const navigate = useNavigate();

  const requestReviewAndNavigate = (
    documentUuid: string,
    approverUuid?: string,
    referenceUserUuid?: string[],
  ) => {
    requestReview(
      {
        documentUuid,
        data: {
          approverUuids: approverUuid ? [approverUuid] : undefined,
          referenceUserUuids: referenceUserUuid ? referenceUserUuid : undefined,
        },
      },
      {
        onSuccess: () => {
          onOpenStep1CompleteToast(() => {
            setBeforeUnloadDisable(true);
            navigate(
              ROUTER_PATH.myPage.desk +
                "?selectedTab.name=ANALYSIS&selectedTab.uuid=" +
                DOCUMENT_CATEGORY_UUID.ANALYSIS +
                "&page=1",
            );
          });
        },
      },
    );
  };

  const onClickStep1Complete = methods.handleSubmit((form: DocumentForm) => {
    if (!documentClassUuid) return;

    const approverUuid = form[DECISION_MANAGER_FIELD_NAME];
    const referenceUserUuid = form[REFERENCE_MEMBER_FIELD_NAME];

    const saveRequest = getTempSaveRequest({
      tempDocumentDetail,
      documentClassUuid,
      documentClassName,
      form,
      tagUuids,
      freeTagUuids: getFreeTagUuids(),
      provinceName: selectedProvince,
      cityName: selectedCity || undefined,
      lastLevel: 1,
    });

    if (!tempDocumentDetail) {
      saveTempDoc({
        saveRequest,
        successCallback(documentUuid) {
          requestReviewAndNavigate(
            documentUuid,
            approverUuid,
            referenceUserUuid,
          );
        },
      });
      return;
    }

    const updateRequest = getTempUpdateRequest({
      tempDocumentDetail,
      form,
      tagUuids,
      freeTagUuids: getFreeTagUuids(),
      provinceName: selectedProvince,
      cityName: selectedCity || undefined,
      lastLevel: 1,
    });

    updateTempDoc({
      documentUuid: tempDocumentDetail.uuid,
      updateRequest,
      successCallback(documentUuid) {
        requestReviewAndNavigate(documentUuid, approverUuid, referenceUserUuid);
      },
    });
  });

  return {
    openStep1CompleteToast,
    onOpenChangeStep1CompleteToast,
    onClickStep1Complete,
  };
};

export const WrapStyle = styled.div`
  width: 100.6rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;
